/**
 * The single shared REST error handler (issue #1963, Phase 1).
 *
 * Replaces the ~287 ad-hoc try/catch blocks each re-implementing error UI
 * (epic #1961). Callers route a caught failure through here: it normalizes
 * the value into an {@link ApiError}, logs it with the request id, surfaces a
 * toast via the global notification store, and returns the normalized error
 * so the caller can still branch on `status` / `requestId`.
 *
 * Aborted/teardown requests are benign (the caller walked away) and never
 * produce a toast.
 */

import { getLogger } from "@/utils/logger";

import { ApiError, toApiError } from "./ApiError";
import { useNotificationStore } from "./notificationStore";

const logger = getLogger("ApiError");

export interface HandleApiErrorOptions {
  /** Message shown when the error carries none of its own. */
  fallbackMessage?: string;
  /** Normalize + log + return, but don't show a notification. */
  silent?: boolean;
}

export function handleApiError(error: unknown, options: HandleApiErrorOptions = {}): ApiError {
  const apiError = toApiError(error, options.fallbackMessage);

  // A deliberately aborted / torn-down request is not a failure the user
  // needs to see — the caller no longer cares about the result.
  if (apiError.aborted) {
    return apiError;
  }

  logger.error(apiError.message, {
    status: apiError.status,
    requestId: apiError.requestId,
    cause: apiError.originalError,
  });

  if (!options.silent) {
    useNotificationStore().notifyError(apiError.message, { requestId: apiError.requestId });
  }

  return apiError;
}
